import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';

import { Course, CourseEnrollmentStatusEnum } from 'commons/types';

import { useEntityTable, useFilters } from 'utils/customHooks/table';

import { useGetPaginatedTermsQuery } from 'lib/Api';

import {
  CellText,
  CellTextLink,
} from 'screens/Dashboard/InstanceDashboard/InstanceDashboard.styles';
import PaginatedTable from 'components/PaginatedTable';
import NooderaButton from 'components/NooderaButton';
import FilterDropdown from 'components/FilterDropdown';

import { StyledPrimaryButton, StyledInvertedButton, StyledTag } from './CoursesTable.styles';
import Header from './Header';
import { getTermOptions, modalityOptions } from 'utils/filtersHelpers';
import dayjs from 'dayjs';
import { calculateCourseStatus, MODALITY_DISPLAY_NAME } from 'utils/courseHelpers';
import { titleFormat } from 'utils/string';

const CoursesTable: React.FC = () => {
  const history = useHistory();

  const { data: globalPartner, isLoading: isPartnerLoading } = useSelector(
    (state) => state.partnerInstance.globalPartner,
  );

  const { id: currentUserId } = useSelector((state: any) => state.user);

  const { data: terms, isLoading: isTermsLoading } = useGetPaginatedTermsQuery(
    {
      page: 1,
      perPage: -1,
      sort: 'start_date',
      partnerId: globalPartner?.parentPartnerId,
    },
    { skip: isPartnerLoading },
  );

  const {
    searchInputRef,
    tableParams,
    setTableParams,
    isFetching,
    isLoading,
    paginatedEntities,
    handleSearchChange,
    handleReset,
  } = useEntityTable<Course>({
    entityType: 'user_courses',
    queryArgs: {
      userId: currentUserId,
      include: 'course_progress',
    },
  });
  const { filters, handleFilterChange, resetFilters } = useFilters({
    tableParams,
    setTableParams,
    filtersConfig: {
      term: 'term.id',
      modality: 'modality',
    },
  });

  const enrollmentStatusColours = {
    [CourseEnrollmentStatusEnum.ENROLLED]: '#DD9BF5',
    [CourseEnrollmentStatusEnum.IN_PROGRESS]: '#56CCF2',
    [CourseEnrollmentStatusEnum.COMPLETED]: '#6FCF97',
  };

  const handleCourseRedirect = (course: Course): void => {
    if (course?.courseUrl) {
      window.location.href = course?.courseUrl;
    }
  };

  const getActionButton = (course: Course): React.ReactNode => {
    const courseStatus = calculateCourseStatus(course, true);
    const onClickHandler = () => handleCourseRedirect(course);
    if (courseStatus === CourseEnrollmentStatusEnum.IN_PROGRESS) {
      return <StyledPrimaryButton onClick={onClickHandler}>Resume Course</StyledPrimaryButton>;
    }

    // Prohibit access to the LMS outside of the LMS access date window
    if (
      (course.lmsAccessStartDate && dayjs(course.lmsAccessStartDate) > dayjs()) ||
      (course.lmsAccessEndDate && dayjs(course.lmsAccessEndDate) < dayjs())
    ) {
      return (
        <StyledInvertedButton onClick={() => history.push(`/courses/${course.slug}`)}>
          View Details
        </StyledInvertedButton>
      );
    }

    // Permit access to self-paced courses if the user is enrolled
    if (course.isSelfPaced && courseStatus === CourseEnrollmentStatusEnum.ENROLLED) {
      return <StyledPrimaryButton onClick={onClickHandler}>Start Course</StyledPrimaryButton>;
    }

    // Prohibit access to the LMS for users that have only paid a deposit
    if (courseStatus === CourseEnrollmentStatusEnum.DEPOSITED) {
      return (
        <StyledInvertedButton onClick={() => history.push(`/courses/${course.slug}`)}>
          View Details
        </StyledInvertedButton>
      );
    }

    if (
      courseStatus === CourseEnrollmentStatusEnum.COMPLETED ||
      (dayjs(course.courseEndDate) < dayjs() &&
        ((course.lmsAccessEndDate && dayjs(course.lmsAccessEndDate) > dayjs()) ||
          course.lmsIndefiniteAccess))
    ) {
      return <StyledInvertedButton onClick={onClickHandler}>View Course</StyledInvertedButton>;
    }

    if (
      (courseStatus === CourseEnrollmentStatusEnum.ENROLLED &&
        dayjs(course.courseStartDate) <= dayjs()) ||
      (course.isSelfPaced && (!course.courseEndDate || dayjs(course.courseEndDate) <= dayjs()))
    ) {
      if (!course.courseEndDate || dayjs(course.courseEndDate) >= dayjs()) {
        return <StyledPrimaryButton onClick={onClickHandler}>Start Course</StyledPrimaryButton>;
      }
    }

    return (
      <StyledInvertedButton onClick={() => history.push(`/courses/${course.slug}`)}>
        View Details
      </StyledInvertedButton>
    );
  };

  const columns: ColumnsType<Course> = [
    {
      title: 'Name',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
      render: (_, record) => (
        <CellTextLink to={`/courses/${record.slug}`}>{record?.shortTitle}</CellTextLink>
      ),
    },
    {
      title: 'Terms',
      dataIndex: ['terms'],
      key: 'terms',
      sorter: true,
      sortKey: 'term.name',
      render: (term: string) => <CellText>{term || '-'}</CellText>,
      filterDropdown: () => (
        <FilterDropdown
          placeholder='Filter by Term'
          loading={isFetching}
          onChange={handleFilterChange('term')}
          value={filters.term?.value || undefined}
          options={getTermOptions(terms)}
        />
      ),
      filtered: !!filters.term?.value,
    },
    {
      title: 'Start Date',
      dataIndex: 'courseStartDate',
      key: 'courseStartDate',
      sorter: true,
      render: (startDate: string, record: Course) => (
        <CellText>
          {startDate ? moment.tz(startDate, record?.timezone).format('MM/DD/YYYY') : '-'}
        </CellText>
      ),
    },
    {
      title: 'End Date',
      dataIndex: 'courseEndDate',
      key: 'courseEndDate',
      sorter: true,
      render: (endDate: string, record: Course) => (
        <CellText>
          {endDate ? moment.tz(endDate, record?.timezone).format('MM/DD/YYYY') : '-'}
        </CellText>
      ),
    },
    {
      title: 'Modality',
      dataIndex: 'courseModality',
      key: 'courseModality',
      sorter: true,
      render: (modality: string[]) => (
        <CellText>{modality?.map((item) => MODALITY_DISPLAY_NAME[item]) || '-'}</CellText>
      ),
      filterDropdown: () => (
        <FilterDropdown
          placeholder='Filter by Modality'
          loading={isFetching}
          onChange={handleFilterChange('modality', 'any')}
          value={filters.modality?.value || undefined}
          options={modalityOptions}
        />
      ),
      filtered: !!filters.modality?.value,
    },
    {
      title: 'Course Status',
      key: 'courseStatus',
      render: (record: Course) => {
        const courseStatus = calculateCourseStatus(record);
        return record.isLmsHosted ? (
          <StyledTag color={enrollmentStatusColours[courseStatus]} style={{ color: '#000' }}>
            {titleFormat(courseStatus)}
          </StyledTag>
        ) : (
          '-'
        );
      },
    },
    {
      title: 'Action',
      key: 'actions',
      render: (_, record) => (record.isLmsHosted ? getActionButton(record) : '-'),
    },
  ];
  return (
    <>
      <Header handleSearchChange={handleSearchChange} searchInputRef={searchInputRef} />

      <PaginatedTable<Course>
        dataSource={paginatedEntities?.data}
        columns={columns}
        fetching={isFetching}
        loading={isLoading || isTermsLoading}
        total={paginatedEntities?.pagination?.total}
        setTableParams={setTableParams}
        tableParams={tableParams}
        rowKey='id'
        size='small'
        locale={{
          emptyText: (
            <Empty
              description={
                tableParams.search
                  ? "We couldn't find any matches for your search. Try broadening your search terms"
                  : 'No Data'
              }
            >
              {tableParams.search && (
                <NooderaButton.CTA
                  size='medium'
                  onClick={() => {
                    handleReset();
                    resetFilters();
                  }}
                  aria-label='Reset and Show All Records'
                >
                  Reset and Show All Records
                </NooderaButton.CTA>
              )}
            </Empty>
          ),
        }}
      />
    </>
  );
};

export default CoursesTable;
